import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Wifi, WifiOff, Globe, Router, Shield, Signal, Lock } from 'lucide-react';

export const NetworkSettings: React.FC = () => {
  const [wifiEnabled, setWifiEnabled] = useState(true);
  const [ethernetEnabled, setEthernetEnabled] = useState(true);
  const [proxyEnabled, setProxyEnabled] = useState(false);
  const [dhcpEnabled, setDhcpEnabled] = useState(true);
  const [ipAddress, setIpAddress] = useState('192.168.1.100');
  const [subnetMask, setSubnetMask] = useState('255.255.255.0');
  const [gateway, setGateway] = useState('192.168.1.1');
  const [dns1, setDns1] = useState('8.8.8.8');
  const [dns2, setDns2] = useState('8.8.4.4');

  const mockWifiNetworks = [
    { name: 'HomeNetwork_5G', signal: 95, secured: true, connected: true },
    { name: 'Office_WiFi', signal: 78, secured: true, connected: false },
    { name: 'Guest_Network', signal: 62, secured: false, connected: false },
    { name: 'Neighbor_WiFi', signal: 45, secured: true, connected: false },
    { name: 'CoffeeShop_Free', signal: 30, secured: false, connected: false },
  ];

  const getSignalIcon = (strength: number) => {
    if (strength >= 75) return <Signal className="text-green-500" size={16} />;
    if (strength >= 50) return <Signal className="text-yellow-500" size={16} />;
    return <Signal className="text-red-500" size={16} />;
  };

  return (
    <div className="h-full p-6 bg-background overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">Network Settings</h1>
          <p className="text-muted-foreground">
            Configure network connections and internet settings
          </p>
        </div>

        {/* Connection Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe size={20} />
              Connection Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <div>
                  <div className="font-medium text-green-700 dark:text-green-300">Connected</div>
                  <div className="text-sm text-green-600 dark:text-green-400">Internet access available</div>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200">
                Active
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">IP Address:</span>
                <div className="font-mono">{ipAddress}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Gateway:</span>
                <div className="font-mono">{gateway}</div>
              </div>
              <div>
                <span className="text-muted-foreground">DNS:</span>
                <div className="font-mono">{dns1}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Connection:</span>
                <div>Wi-Fi (HomeNetwork_5G)</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wi-Fi Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {wifiEnabled ? <Wifi size={20} /> : <WifiOff size={20} />}
              Wi-Fi
            </CardTitle>
            <CardDescription>
              Manage wireless network connections
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="wifi-enabled">Enable Wi-Fi</Label>
              <Switch
                id="wifi-enabled"
                checked={wifiEnabled}
                onCheckedChange={setWifiEnabled}
              />
            </div>

            {wifiEnabled && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Available Networks</h4>
                    <Button variant="outline" size="sm">
                      Refresh
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {mockWifiNetworks.map((network, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          network.connected 
                            ? 'bg-primary/5 border-primary/20' 
                            : 'hover:bg-muted/50 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {getSignalIcon(network.signal)}
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {network.name}
                              {network.secured && <Lock size={12} className="text-muted-foreground" />}
                              {network.connected && (
                                <Badge variant="secondary" className="text-xs">Connected</Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {network.secured ? 'Secured' : 'Open'} • Signal: {network.signal}%
                            </div>
                          </div>
                        </div>
                        {!network.connected && (
                          <Button variant="outline" size="sm">
                            Connect
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Ethernet Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Router size={20} />
              Ethernet
            </CardTitle>
            <CardDescription>
              Wired network connection settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="ethernet-enabled">Enable Ethernet</Label>
              <Switch
                id="ethernet-enabled"
                checked={ethernetEnabled}
                onCheckedChange={setEthernetEnabled}
              />
            </div>

            {ethernetEnabled && (
              <>
                <Separator />
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="dhcp-enabled">Obtain IP address automatically (DHCP)</Label>
                  <Switch
                    id="dhcp-enabled"
                    checked={dhcpEnabled}
                    onCheckedChange={setDhcpEnabled}
                  />
                </div>

                {!dhcpEnabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ip-address">IP Address</Label>
                      <Input
                        id="ip-address"
                        value={ipAddress}
                        onChange={(e) => setIpAddress(e.target.value)}
                        placeholder="192.168.1.100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subnet-mask">Subnet Mask</Label>
                      <Input
                        id="subnet-mask"
                        value={subnetMask}
                        onChange={(e) => setSubnetMask(e.target.value)}
                        placeholder="255.255.255.0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gateway">Default Gateway</Label>
                      <Input
                        id="gateway"
                        value={gateway}
                        onChange={(e) => setGateway(e.target.value)}
                        placeholder="192.168.1.1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dns1">Primary DNS</Label>
                      <Input
                        id="dns1"
                        value={dns1}
                        onChange={(e) => setDns1(e.target.value)}
                        placeholder="8.8.8.8"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Proxy Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield size={20} />
              Proxy Settings
            </CardTitle>
            <CardDescription>
              Configure proxy server for internet connections
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="proxy-enabled">Use proxy server</Label>
              <Switch
                id="proxy-enabled"
                checked={proxyEnabled}
                onCheckedChange={setProxyEnabled}
              />
            </div>

            {proxyEnabled && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="proxy-address">Proxy Address</Label>
                  <Input
                    id="proxy-address"
                    placeholder="proxy.example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="proxy-port">Port</Label>
                  <Input
                    id="proxy-port"
                    placeholder="8080"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Network Diagnostics */}
        <Card>
          <CardHeader>
            <CardTitle>Network Diagnostics</CardTitle>
            <CardDescription>
              Test and troubleshoot network connectivity
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm">
                Test Internet Connection
              </Button>
              <Button variant="outline" size="sm">
                Ping Gateway
              </Button>
              <Button variant="outline" size="sm">
                DNS Lookup Test
              </Button>
              <Button variant="outline" size="sm">
                Speed Test
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline">Reset to Defaults</Button>
          <Button>Apply Changes</Button>
        </div>
      </div>
    </div>
  );
};